import { groupBy } from 'lodash-es'

import { getMappedColors } from '../colorsMap'
import {
  ColumnType,
  DateFormat,
  getAxisLabelFormatter,
  NumberFormat,
} from '../formatChartData'
import { isDevEnv } from '@/utils/isRuntimeEnv'
import { getCurrentLocale } from '@/utils/localeMapping'

export interface ChartDataPoint {
  group?: number
  x?: number
  value: number
  color?: number
  type?: string
}

export interface FilterOption {
  _id: string
  name: string
}

export interface ChartFilter {
  _id: string
  name: string
  options: FilterOption[]
  currentId: string
}

export interface MetaData {
  elementType: string
  title: string
  subTitle?: string
  legendPosition?: string
  colors?: string[]
  noDataMessage?: string
  filters?: ChartFilter[]
  properties: {
    groupTitles?: string[]
    groupLine?: boolean[]
    xTitles?: string[]
    valueLabel?: boolean
    xFormat?: NumberFormat
    yFormat?: NumberFormat
    columnTitles?: string[]
    columnTypes?: ColumnType[]
    columnStyles?: Array<{
      foreground?: string
      font?: string
      format?: DateFormat & NumberFormat
    }>
    rowLayout?: number[][]
  }
}

export interface ChartData {
  _id: string
  metaData: MetaData
  chartData?: ChartDataPoint[]
  listData?: Array<{ data: string[] }>
  rawData?: string[]
}

export function getBarChartOptionsAndData(
  data: ChartData,
  showLegend: boolean,
) {
  const locale = getCurrentLocale()

  try {
    const { metaData, chartData = [] } = data

    if (isDevEnv()) {
      console.log(
        `>>> Raw V2 chart data for chart "${metaData.title}":`,
        chartData,
      )
    }

    const defaultFormatter = (value: number) => value.toLocaleString(locale)
    const formatY = getAxisLabelFormatter(
      metaData.properties.yFormat,
      locale,
      defaultFormatter,
    )
    const formatX = getAxisLabelFormatter(
      metaData.properties.xFormat,
      locale,
      defaultFormatter,
    )

    // Handle empty chart data
    if (chartData.length === 0) {
      const emptyOptions = {
        chart: {
          type: 'bar',
          width: '100%',
          height: '100%',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 5,
            borderRadiusApplication: 'end',
            distributed: false,
          },
        },
        xaxis: {
          labels: {
            formatter: formatX,
            show: true,
          },
          categories: [],
        },
        yaxis: {
          labels: {
            formatter: formatY,
          },
        },
        colors: getMappedColors(metaData.colors),
        legend: {
          show: false,
          position: (metaData.legendPosition || 'bottom') as
            | 'top'
            | 'right'
            | 'bottom'
            | 'left',
        },
        title: {
          text: '',
          align: 'left',
        },
        tooltip: {
          y: {
            formatter: formatY,
          },
        },
        noData: {
          text: metaData.noDataMessage || 'No data found',
        },
      }

      return { options: emptyOptions, data: [] }
    }

    // Determine chart type
    const isStacked = metaData.elementType === 'stackedBarChart'
    const categories = metaData.properties.xTitles || []
    const hasXAxis = categories.length > 0
    const groupTitles = metaData.properties.groupTitles || []

    // Check if this is a single bar chart (no x-axis, uses group as category index)
    // OR if there's data but no groupTitles/xTitles (display single value)
    const isSingleBar =
      !hasXAxis && (groupTitles.length > 0 || chartData.length > 0)

    let series: {
      name: string
      data: number[]
      color?: string
      type?: string
    }[]

    if (isSingleBar) {
      // Single bar chart - use groupTitles as categories and values directly
      const categoryNames = metaData.properties.groupTitles || []

      // If no groupTitles but we have data, use "Total" or empty string for single values
      const effectiveCategoryNames =
        categoryNames.length > 0 ? categoryNames : chartData.map(() => '')

      // Sort by group index to ensure correct order
      const sortedData = [...chartData].sort(
        (a, b) => (a.group || 0) - (b.group || 0),
      )
      const values = sortedData.map((point) => point.value)
      const colors = getMappedColors(metaData.colors)

      series = [
        {
          name: metaData.title,
          data: values,
        },
      ]

      // Use distributed colors for single bar charts
      // Enable data labels if there's only one value
      const showDataLabels = chartData.length === 1

      const defOptions = {
        chart: {
          type: 'bar',
          width: '100%',
          height: '100%',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: showDataLabels,
          formatter: formatY,
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            colors: ['#fff'],
          },
          offsetY: 0,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 5,
            borderRadiusApplication: 'end',
            distributed: true, // Each bar gets its own color
          },
        },
        xaxis: {
          labels: {
            show: true, // Show x-axis labels (groupTitles like "1.2024", "34.2025", etc.)
          },
          categories: effectiveCategoryNames,
        },
        yaxis: {
          labels: {
            formatter: formatY,
          },
        },
        colors: colors,
        legend: {
          show: false, // No legend for single bar
          position: (metaData.legendPosition || 'bottom') as
            | 'top'
            | 'right'
            | 'bottom'
            | 'left',
        },
        title: {
          text: '',
          align: 'left',
        },
        tooltip: {
          y: {
            formatter: formatY,
          },
        },
        noData: {
          text: metaData.noDataMessage || 'No data found',
        },
      }

      if (isDevEnv()) {
        console.log(
          `>>> Processed V2 chart data for chart "${metaData.title}":`,
          series,
        )
      }

      return { options: defOptions, data: series }
    }

    // Multi-bar chart with x-axis
    // Group chart data by group index
    const groupedData = groupBy(chartData, 'group')
    const mappedColors = getMappedColors(metaData.colors)
    const groupLine = metaData.properties.groupLine || []

    // Create series for each group
    series = Object.keys(groupedData)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((groupIndex) => {
        const groupNum = parseInt(groupIndex)
        const dataPoints = groupedData[groupIndex]
        const isLine = groupLine[groupNum] === true

        if (!dataPoints) {
          return {
            name:
              (metaData.properties.groupTitles &&
                metaData.properties.groupTitles[groupNum]) ||
              '',
            data: [],
            color: mappedColors[groupNum],
            type: isLine ? 'line' : 'column',
          }
        }

        // Sort by x index and extract values
        const sortedData = dataPoints.sort((a, b) => {
          const aX = a.x !== undefined ? a.x : 0
          const bX = b.x !== undefined ? b.x : 0
          return aX - bX
        })

        // Create array with values at correct x positions
        const data = new Array(categories.length).fill(0)
        sortedData.forEach((point) => {
          if (point.x !== undefined && point.x < categories.length) {
            data[point.x] = point.value
          }
        })

        return {
          name:
            (metaData.properties.groupTitles &&
              metaData.properties.groupTitles[groupNum]) ||
            '',
          data,
          color: mappedColors[groupNum],
          type: isLine ? 'line' : 'column',
        }
      })

    // Map hex colors to CSS variables
    const colors = mappedColors

    // Check if any series is a line
    const hasLineSeries = series.some((s) => s.type === 'line')

    // Configure stroke width and markers based on groupLine settings
    const strokeWidths = series.map((s) => (s.type === 'line' ? 4 : 0))
    const markerSizes = series.map((s) => (s.type === 'line' ? 6 : 0))

    const defOptions = {
      chart: {
        type: hasLineSeries ? 'line' : 'bar',
        width: '100%',
        height: '100%',
        stacked: isStacked ? true : undefined,
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: strokeWidths,
        curve: 'straight',
      },
      markers: {
        size: markerSizes,
        strokeWidth: 0,
        hover: {
          size: 8,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 5,
          borderRadiusApplication: 'end',
          distributed: false,
        },
      },
      xaxis: {
        labels: {
          show: true,
        },
        categories: categories,
      },
      yaxis: {
        labels: {
          formatter: formatY,
        },
      },
      colors: colors,
      legend: {
        show: showLegend,
        position: (metaData.legendPosition || 'bottom') as
          | 'top'
          | 'right'
          | 'bottom'
          | 'left',
      },
      title: {
        text: '',
        align: 'left',
      },
      tooltip: {
        y: {
          formatter: formatY,
        },
      },
      noData: {
        text: metaData.noDataMessage || 'No data found',
      },
    }

    if (isDevEnv()) {
      console.log(
        `>>> Processed V2 chart data for chart "${metaData.title}":`,
        series,
      )
    }

    return { options: defOptions, data: series }
  } catch (error) {
    console.error(
      'Error processing bar chart data:',
      error instanceof Error ? error.message : 'Unknown error',
    )

    // Return safe fallback configuration
    const fallbackOptions = {
      chart: {
        type: 'bar',
        width: '100%',
        height: '100%',
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 5,
          borderRadiusApplication: 'end',
          distributed: false,
        },
      },
      xaxis: {
        labels: {
          formatter: (value: number) => value.toLocaleString(locale),
          show: true,
        },
        categories: [],
      },
      yaxis: {
        labels: {
          formatter: (value: number) => value.toLocaleString(locale),
        },
      },
      colors: [],
      legend: {
        show: false,
        position: 'bottom' as 'top' | 'right' | 'bottom' | 'left',
      },
      title: {
        text: '',
        align: 'left',
      },
      tooltip: {
        y: {
          formatter: (value: number) => value.toLocaleString(locale),
        },
      },
      noData: {
        text: 'Error loading chart data',
      },
    }

    return { options: fallbackOptions, data: [] }
  }
}
